import React, { useState } from "react";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
	faChevronDown,
	faUser,
	faBookOpen,
	faCalendarAlt,
	faSpinner,
	faExclamationTriangle,
} from "@fortawesome/free-solid-svg-icons";
import { getSemesterMonths, getMaxPoints } from "../utils";
import { VIEW_MODES } from "../constants";
import styles from "./MobileGradesView.module.css";

const MobileGradesView = ({
	viewMode,
	selectedSubject,
	selectedStudent,
	selectedSemester,
	searchTerm,
	setSearchTerm,
	students,
	departmentSubjects,
	grades,
	classData,
	isLoadingGrades,
	gradesError,
	onSubjectSelect,
	onStudentSelect,
	onGradeCellClick,
	onSemesterChange,
	onViewModeChange,
	calculateTotal,
}) => {
	const [showSelector, setShowSelector] = useState(false);
	const [selectedMonth, setSelectedMonth] = useState(null);

	const semesterMonths = getSemesterMonths(selectedSemester);

	// Get the current selection data
	const currentSelection =
		viewMode === VIEW_MODES.SUBJECTS ? selectedSubject : selectedStudent;
	const availableOptions =
		viewMode === VIEW_MODES.SUBJECTS ? departmentSubjects : students;
	const selectionType =
		viewMode === VIEW_MODES.SUBJECTS ? "subject" : "student";

	// Filter students or get subject data based on view mode
	const displayData =
		viewMode === VIEW_MODES.SUBJECTS && selectedSubject
			? students.filter(
					(student) =>
						student.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
						student.studentId.toLowerCase().includes(searchTerm.toLowerCase())
			  )
			: selectedStudent && departmentSubjects
			? departmentSubjects.filter((subject) =>
					subject.name.toLowerCase().includes(searchTerm.toLowerCase())
			  )
			: [];

	const handleSelectionChange = (option) => {
		if (viewMode === VIEW_MODES.SUBJECTS) {
			onSubjectSelect(option);
		} else {
			onStudentSelect(option);
		}
		setShowSelector(false);
	};

	const handleMonthSelect = (month) => {
		setSelectedMonth(selectedMonth?.id === month.id ? null : month);
	};

	// Get grade data for display - FIXED VERSION
	const getGradeData = (itemId, monthId = null) => {
		if (viewMode === VIEW_MODES.SUBJECTS && selectedSubject) {
			// For subjects view, just use the student ID directly
			const studentGrades = grades[itemId];
			if (monthId) {
				// Access the month data directly
				return studentGrades?.[monthId];
			}
			return studentGrades;
		} else if (viewMode === VIEW_MODES.STUDENTS && selectedStudent) {
			// For students view, use student_subject key
			const subjectKey = `${selectedStudent.id}_${itemId}`;
			const subjectGrades = grades[subjectKey];
			if (monthId) {
				return subjectGrades?.months?.[monthId];
			}
			return subjectGrades;
		}
		return null;
	};

	if (isLoadingGrades) {
		return (
			<div className={styles.loadingContainer}>
				<FontAwesomeIcon icon={faSpinner} className={styles.spinner} spin />
				<p>Loading grades...</p>
			</div>
		);
	}

	if (gradesError) {
		return (
			<div className={styles.errorContainer}>
				<FontAwesomeIcon
					icon={faExclamationTriangle}
					className={styles.errorIcon}
				/>
				<p>Error loading grades. Please try again.</p>
			</div>
		);
	}

	return (
		<div className={styles.mobileGradesView}>
			{/* Top Controls Row - View Mode and Semester */}
			<div className={styles.topControls}>
				{/* View Mode Switch */}
				<div className={styles.viewModeSwitch}>
					<div className={styles.viewModeTabs}>
						<button
							className={`${styles.viewModeTab} ${
								viewMode === VIEW_MODES.SUBJECTS ? styles.active : ""
							}`}
							onClick={() => onViewModeChange(VIEW_MODES.SUBJECTS)}
						>
							Subjects
						</button>
						<button
							className={`${styles.viewModeTab} ${
								viewMode === VIEW_MODES.STUDENTS ? styles.active : ""
							}`}
							onClick={() => onViewModeChange(VIEW_MODES.STUDENTS)}
						>
							Students
						</button>
					</div>
				</div>

				{/* Semester Switch */}
				<div className={styles.semesterSwitch}>
					<div className={styles.semesterTabs}>
						{[1, 2].map((semester) => (
							<button
								key={semester}
								className={`${styles.semesterTab} ${
									selectedSemester === semester ? styles.active : ""
								}`}
								onClick={() => onSemesterChange(semester)}
							>
								Semester {semester}
							</button>
						))}
					</div>
				</div>
			</div>

			{/* Selection Dropdown */}
			<div className={styles.selectorContainer}>
				<button
					className={styles.selectorButton}
					onClick={() => setShowSelector(!showSelector)}
				>
					<div className={styles.selectorContent}>
						<FontAwesomeIcon
							icon={viewMode === VIEW_MODES.SUBJECTS ? faBookOpen : faUser}
							className={styles.selectorIcon}
						/>
						<div className={styles.selectorText}>
							<span className={styles.selectorLabel}>
								{viewMode === VIEW_MODES.SUBJECTS ? "Subject" : "Student"}
							</span>
							<span className={styles.selectorValue}>
								{currentSelection
									? currentSelection.name
									: `Select ${selectionType}`}
							</span>
						</div>
					</div>
					<FontAwesomeIcon
						icon={faChevronDown}
						className={`${styles.chevron} ${showSelector ? styles.open : ""}`}
					/>
				</button>

				{/* Dropdown Menu */}
				{showSelector && (
					<div className={styles.dropdown}>
						<div className={styles.dropdownHeader}>
							<input
								type="text"
								placeholder={`Search ${selectionType}s...`}
								value={searchTerm}
								onChange={(e) => setSearchTerm(e.target.value)}
								className={styles.searchInput}
							/>
						</div>
						<div className={styles.dropdownList}>
							{availableOptions
								.filter(
									(option) =>
										option.name
											.toLowerCase()
											.includes(searchTerm.toLowerCase()) ||
										(viewMode === VIEW_MODES.STUDENTS &&
											option.studentId
												.toLowerCase()
												.includes(searchTerm.toLowerCase()))
								)
								.map((option) => (
									<button
										key={option.id}
										className={`${styles.dropdownItem} ${
											currentSelection?.id === option.id ? styles.selected : ""
										}`}
										onClick={() => handleSelectionChange(option)}
									>
										<span className={styles.optionName}>{option.name}</span>
										{viewMode === VIEW_MODES.STUDENTS && (
											<span className={styles.optionId}>
												{option.studentId}
											</span>
										)}
										{viewMode === VIEW_MODES.SUBJECTS &&
											option.teacherAssigned && (
												<span className={styles.assignedBadge}>Assigned</span>
											)}
									</button>
								))}
						</div>
					</div>
				)}
			</div>

			{/* Search Input */}
			<div className={styles.searchContainer}>
				<input
					type="text"
					placeholder={`Search ${
						viewMode === VIEW_MODES.SUBJECTS ? "students" : "subjects"
					}...`}
					value={searchTerm}
					onChange={(e) => setSearchTerm(e.target.value)}
					className={styles.searchInput}
				/>
			</div>

			{/* Grades Display Area */}
			{!currentSelection ? (
				<div className={styles.emptyState}>
					<FontAwesomeIcon
						icon={viewMode === VIEW_MODES.SUBJECTS ? faBookOpen : faUser}
						className={styles.emptyIcon}
					/>
					<h3>
						Select {selectionType === "subject" ? "a Subject" : "a Student"}
					</h3>
					<p>
						Choose {selectionType === "subject" ? "a subject" : "a student"}{" "}
						from the dropdown to view grades.
					</p>
				</div>
			) : displayData.length === 0 ? (
				<div className={styles.emptyState}>
					<h3>No Data Available</h3>
					<p>
						No {viewMode === VIEW_MODES.SUBJECTS ? "students" : "subjects"}{" "}
						found.
					</p>
				</div>
			) : (
				<>
					{/* Month Selector */}
					<div className={styles.monthSelector}>
						<h4 className={styles.monthSelectorTitle}>
							<FontAwesomeIcon icon={faCalendarAlt} />
							Select Month
						</h4>
						<div className={styles.monthTabs}>
							{semesterMonths.map((month) => (
								<button
									key={month.id}
									className={`${styles.monthTab} ${
										selectedMonth?.id === month.id ? styles.active : ""
									}`}
									onClick={() => handleMonthSelect(month)}
								>
									{month.short}
								</button>
							))}
						</div>
					</div>

					{/* Grades Display */}
					{selectedMonth ? (
						// Individual month view
						<div className={styles.monthGrades}>
							<div className={styles.monthHeader}>
								<h3>{selectedMonth.name} Grades</h3>
								<span className={styles.maxPoints}>
									Max: {getMaxPoints(classData.gradeLevel, selectedMonth.id)}{" "}
									points
								</span>
							</div>

							<div className={styles.gradesList}>
								{displayData.map((item) => {
									const gradeData = getGradeData(item.id, selectedMonth.id);
									const maxPoints = getMaxPoints(
										classData.gradeLevel,
										selectedMonth.id
									);
									const hasGrades = gradeData && gradeData.total > 0;
									const isLowGrade =
										gradeData && gradeData.total / maxPoints < 0.5;

									return (
										<div
											key={item.id}
											className={`${styles.gradeItem} ${
												hasGrades ? styles.hasGrades : styles.noGrades
											} ${isLowGrade ? styles.lowGrade : ""}`}
											onClick={() => {
												if (
													viewMode === VIEW_MODES.SUBJECTS &&
													onGradeCellClick
												) {
													onGradeCellClick(item.id, selectedMonth.id);
												}
											}}
										>
											<div className={styles.gradeItemHeader}>
												<span className={styles.itemName}>{item.name}</span>
												{viewMode === VIEW_MODES.STUDENTS ? (
													<span className={styles.itemId}>
														Grade {classData.gradeLevel}
													</span>
												) : (
													<span className={styles.itemId}>
														{item.studentId}
													</span>
												)}
											</div>
											<div className={styles.gradeValue}>
												<span className={styles.points}>
													{gradeData?.total || 0}
												</span>
												<span className={styles.maxPointsText}>
													/ {maxPoints}
												</span>
											</div>
										</div>
									);
								})}
							</div>
						</div>
					) : (
						// Overview with all months
						<div className={styles.overviewGrades}>
							<h3>Grade Overview</h3>
							<div className={styles.overviewList}>
								{displayData.map((item) => {
									const totalGrade =
										viewMode === VIEW_MODES.SUBJECTS
											? calculateTotal(item.id)
											: (() => {
													// Calculate total for student view
													const subjectKey = `${selectedStudent.id}_${item.id}`;
													return calculateTotal
														? calculateTotal(subjectKey, selectedSemester)
														: 0;
											  })();

									return (
										<div key={item.id} className={styles.overviewItem}>
											<div className={styles.overviewItemHeader}>
												<span className={styles.itemName}>{item.name}</span>
												{viewMode === VIEW_MODES.STUDENTS ? (
													<span className={styles.itemId}>
														Grade {classData.gradeLevel}
													</span>
												) : (
													<span className={styles.itemId}>
														{item.studentId}
													</span>
												)}
											</div>

											<div className={styles.monthGradesRow}>
												{semesterMonths.map((month) => {
													const gradeData = getGradeData(item.id, month.id);
													const maxPoints = getMaxPoints(
														classData.gradeLevel,
														month.id
													);
													const hasGrades = gradeData && gradeData.total > 0;

													return (
														<div
															key={month.id}
															className={styles.monthGradeChip}
														>
															<span className={styles.monthName}>
																{month.short}
															</span>
															<span
																className={`${styles.monthPoints} ${
																	hasGrades ? styles.hasPoints : styles.noPoints
																}`}
															>
																{gradeData?.total || 0}/{maxPoints}
															</span>
														</div>
													);
												})}
											</div>

											<div className={styles.totalGrade}>
												Total: {totalGrade}/100
											</div>
										</div>
									);
								})}
							</div>
						</div>
					)}
				</>
			)}
		</div>
	);
};

export default MobileGradesView;
